import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { View, Text, TouchableOpacity, TextInput, StyleSheet, Animated } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useApi } from "../context/ApiContext";
import { useData } from "../context/DataContext";
const PushNotificationsService = ({ onUpdate, app }) => {
	const { appId } = useParams();
	const { api } = useApi();
	const { refetchData, appData, teamData, loading } = useData();
	const [error, setError] = useState(null);
	const [appConfig, setAppConfig] = useState(null);
	const [showAppleConfigModal, setShowAppleConfigModal] = useState(false);
	const [showErrorDialog, setShowErrorDialog] = useState(false);
	const [currentStep, setCurrentStep] = useState(1);
	const [appleConfigData, setAppleConfigData] = useState({
		keyId: "",
		p8Key: "",
		p8FileName: "",
	});
	const [configSaving, setConfigSaving] = useState(false);
	const [teams, setTeams] = useState([]);
	const [selectedTeam, setSelectedTeam] = useState(null);
	const spinValue = new Animated.Value(0);

	// Create the spinning animation
	useEffect(() => {
		if (configSaving) {
			Animated.loop(
				Animated.timing(spinValue, {
					toValue: 1,
					duration: 1000,
					useNativeDriver: true,
				})
			).start();
		} else {
			spinValue.setValue(0);
		}
	}, [configSaving]);

	useEffect(() => {
		fetchConfig();
	}, [appId]);

	// Fetch teams on component mount
	useEffect(() => {
		fetchTeams();
	}, []);

	// Reset step when modal is opened
	useEffect(() => {
		if (showAppleConfigModal) {
			setCurrentStep(1);
		}
	}, [showAppleConfigModal]);

	// Add effect to handle team selection when teams are loaded
	useEffect(() => {
		if (teams.length > 0 && appConfig?.teamId) {
			const savedTeam = teams.find((team) => team.id === appConfig.teamId);
			if (savedTeam) {
				setSelectedTeam(savedTeam);
			}
		}
	}, [teams, appConfig?.teamId]);

	const fetchConfig = async () => {
		try {
			const response = appData;

			// Find the push_notifications service in the services array
			const pushNotificationsService = response?.services?.find((service) => service.type === "push_notifications");
			const serviceConfig = pushNotificationsService?.config || {};
			const serviceStatus = pushNotificationsService?.status || "inactive";

			// Check if Apple Core Service is active
			const appleCoreService = response?.services?.find((service) => service.type === "apple_developer");
			const isAppleCoreActive = appleCoreService?.status === "active";
			const teamId = serviceConfig.teamId || appleCoreService?.config?.teamId || null;
			// Set app config with either service data or defaults
			setAppConfig({
				name: serviceConfig.appName || response?.name || "",
				bundleId: serviceConfig.bundleId || "",
				teamId: teamId,
				isAppleCoreActive: isAppleCoreActive,
				serviceStatus: serviceStatus,
			});

			setError(null);
		} catch (err) {
			console.error("Error fetching app config:", err);
			setError(err.message);
		}
	};

	const fetchTeams = async () => {
		try {
			const response = teamData;
			console.log("Response", response);

			// The response includes teams with service status information
			// Each team has: id, name, description, role, services, createdAt, updatedAt
			const updatedTeams = response.map((team) => ({
				...team,
				services: team.services || {},
				role: team.role || "member",
			}));
			setTeams(updatedTeams);

			setError(null);
		} catch (err) {
			setError(err.message);
		}
	};

	const spin = spinValue.interpolate({
		inputRange: [0, 1],
		outputRange: ["0deg", "360deg"],
	});

	const handleFileUpload = async (event) => {
		const file = event.target.files[0];
		if (file) {
			try {
				const reader = new FileReader();
				reader.onload = (e) => {
					setAppleConfigData({
						...appleConfigData,
						p8Key: e.target.result,
						p8FileName: file.name,
					});
				};
				reader.readAsText(file);
			} catch (err) {
				setError("Failed to read p8 key file");
			}
		}
	};

	const handleKeyIdChange = (text) => {
		// Reset p8Key if keyId changes
		if (text !== appleConfigData.keyId) {
			setAppleConfigData({
				keyId: text,
				p8Key: "",
				p8FileName: "",
			});
		} else {
			setAppleConfigData({
				...appleConfigData,
				keyId: text,
			});
		}
	};

	const handleAddToApp = async () => {
		try {
			setConfigSaving(true);
			setError(null);

			// Update the app to point to the team with the push notification keys
			await api("/app", {
				method: "POST",
				body: {
					action: "update_app",
					appId: appId,
					updates: {
						service: "push_notifications",
						teamId: selectedTeam.id,
					},
				},
			});
			await Promise.all([refetchData("app"), refetchData("team")]);
		} catch (err) {
			setError(err.message);
		} finally {
			setConfigSaving(false);
		}
	};

	const handleEditAppleConfig = () => {
		if (!appConfig.isAppleCoreActive) {
			setShowErrorDialog(true);
			return;
		}

		const teamApnConfig = selectedTeam?.services?.appleNotificationConfig;
		const hasTeamConfig = teamApnConfig && teamApnConfig.keyId;
		const isServiceActive = appConfig?.serviceStatus === "active";

		// If team has config but service is not active, just add it to the app
		if (hasTeamConfig && !isServiceActive) {
			handleAddToApp();
			return;
		}

		// Otherwise, open the modal to configure or modify
		setAppleConfigData({
			keyId: teamApnConfig?.keyId || "",
			p8Key: "", // We don't get the actual key back for security
			p8FileName: teamApnConfig?.p8FileName || "", // Keep the existing file name
		});

		setShowAppleConfigModal(true);
	};

	const handleSaveAppleConfig = async () => {
		try {
			setConfigSaving(true);

			// Create an object with the Apple push notification configuration
			const appleConfig = {
				keyId: appleConfigData.keyId,
			};

			// Only include p8Key if a new file was uploaded
			if (appleConfigData.p8Key) {
				appleConfig.p8Key = appleConfigData.p8Key;
				appleConfig.p8FileName = appleConfigData.p8FileName;
			}

			// First, update the team with the Apple Push Notifications configuration
			const teamResponse = await api("/team", {
				method: "PUT",
				body: {
					action: "ApplePushNotifications",
					teamId: selectedTeam.id,
					appleNotificationConfig: appleConfig,
				},
			});

			// Then, update the app to point to the team with the push notification keys
			await api("/app", {
				method: "POST",
				body: {
					action: "update_app",
					appId: appId,
					updates: {
						service: "push_notifications",
						teamId: teamResponse.teamId,
					},
				},
			});

			// Refresh the app config and teams
			await Promise.all([refetchData("app"), refetchData("team")]);
			setShowAppleConfigModal(false);
			setError(null);
			onUpdate?.();
		} catch (err) {
			setError(err.message);
		} finally {
			setConfigSaving(false);
		}
	};

	const handleNextStep = () => {
		setCurrentStep(currentStep + 1);
	};

	const handlePrevStep = () => {
		setCurrentStep(currentStep - 1);
	};

	const renderStepIndicator = () => {
		return (
			<View style={styles.stepIndicatorContainer}>
				{[1, 2, 3, 4].map((step) => (
					<View key={step} style={styles.stepIndicatorItem}>
						<View style={[styles.stepDot, currentStep >= step && styles.activeStepDot]} />
						<Text style={[styles.stepText, currentStep >= step && styles.activeStepText]}>Step {step}</Text>
					</View>
				))}
			</View>
		);
	};

	const renderStepOne = () => {
		return (
			<View style={styles.stepContainer}>
				<Text style={styles.stepHeading}>Navigate to Apple Developer Portal</Text>
				<View style={styles.stepContentBox}>
					<Text style={styles.stepInstructions}>First, you'll need to access the Apple Developer Portal to create a new key for Apple Push Notifications.</Text>

					<View style={styles.stepActionContainer}>
						<TouchableOpacity style={styles.stepActionButton} onPress={() => window.open("https://developer.apple.com/account/resources/authkeys/list", "_blank")}>
							<Text style={styles.stepActionButtonText}>Open Apple Developer Portal</Text>
							<Ionicons name="open-outline" size={16} color="#fff" />
						</TouchableOpacity>
					</View>

					<Text style={styles.stepNote}>Once on the Keys page, click the "+" button to create a new key.</Text>
				</View>
			</View>
		);
	};

	const renderStepTwo = () => {
		// Get team name from selected team
		const teamName = selectedTeam?.name || "Your Team";

		return (
			<View style={styles.stepContainer}>
				<Text style={styles.stepHeading}>Configure Key Settings</Text>
				<View style={styles.stepContentBox}>
					<Text style={styles.stepInstructions}>Now, you'll need to configure the key settings in the Apple Developer Portal.</Text>

					<View style={styles.configInstructionItem}>
						<Text style={styles.configInstructionNumber}>1</Text>
						<Text style={styles.configInstructionText}>
							Set the <Text style={styles.highlightText}>Key Name</Text> to:
							<Text style={styles.codeText}>{`${teamName} Key for Executable`}</Text>
						</Text>
					</View>

					<View style={styles.configInstructionItem}>
						<Text style={styles.configInstructionNumber}>2</Text>
						<Text style={styles.configInstructionText}>
							Check the box for <Text style={styles.highlightText}>Apple Push Notifications service (APNs)</Text>
						</Text>
					</View>

					<Text style={styles.stepNote}>
						After configuring these settings, click{" "}
						<View style={styles.appleButtonContainer}>
							<TouchableOpacity style={styles.appleButton}>
								<Text style={styles.appleButtonText}>Configure</Text>
							</TouchableOpacity>
						</View>
						on the APNs row.
					</Text>
				</View>
			</View>
		);
	};

	const renderStepThree = () => {
		return (
			<View style={styles.stepContainer}>
				<Text style={styles.stepHeading}>Set Environment and Restrictions</Text>
				<View style={styles.stepContentBox}>
					<Text style={styles.stepInstructions}>Configure the environment settings and key restrictions.</Text>

					<View style={styles.configInstructionItem}>
						<Text style={styles.configInstructionNumber}>1</Text>
						<Text style={styles.configInstructionText}>
							Set <Text style={styles.highlightText}>Environment</Text> to:
							<Text style={styles.codeText}>Sandbox & Production</Text>
						</Text>
					</View>

					<View style={styles.configInstructionItem}>
						<Text style={styles.configInstructionNumber}>2</Text>
						<Text style={styles.configInstructionText}>
							Set <Text style={styles.highlightText}>Key Restriction</Text> to:
							<Text style={styles.codeText}>Team Scoped (All Topics)</Text>
						</Text>
					</View>

					<View style={styles.configInstructionItem}>
						<Text style={styles.configInstructionNumber}>3</Text>
						<Text style={styles.configInstructionText}>
							Click <Text style={styles.highlightText}>Save</Text> to go back to the configuration list.
						</Text>
					</View>

					<View style={styles.configInstructionItem}>
						<Text style={styles.configInstructionNumber}>4</Text>
						<Text style={styles.configInstructionText}>
							Click <Text style={styles.highlightText}>Register</Text> to complete the key creation process.
						</Text>
					</View>

					<Text style={styles.stepNoteDanger}>Do NOT click "Done". You'll need to download the key and upload it to Executable.</Text>
				</View>
			</View>
		);
	};

	const renderStepFour = () => {
		// Get team info from selected team
		const teamName = selectedTeam?.name || "Your Team";
		const teamId = selectedTeam?.services?.appStore?.teamId || "XXXXXXXXXX";

		return (
			<View style={styles.stepContainer}>
				<Text style={styles.stepHeading}>Enter Key Information</Text>
				<View style={styles.stepContentBox}>
					<Text style={styles.stepInstructions}>Now that you've created your key, enter the Key ID and upload the .p8 file.</Text>

					<View style={styles.teamInfoContainer}>
						<Text style={styles.teamInfoLabel}>Team Information (imported from Apple Core Service)</Text>
						<View style={styles.teamInfoItem}>
							<Text style={styles.teamInfoTitle}>Team Name:</Text>
							<Text style={styles.teamInfoValue}>{teamName}</Text>
							<Ionicons name="lock-closed" size={16} color="#999" style={styles.teamInfoLock} />
						</View>
						<View style={styles.teamInfoItem}>
							<Text style={styles.teamInfoTitle}>Team ID:</Text>
							<Text style={styles.teamInfoValue}>{teamId}</Text>
							<Ionicons name="lock-closed" size={16} color="#999" style={styles.teamInfoLock} />
						</View>
					</View>

					<View style={styles.formGroup}>
						<Text style={styles.formLabel}>Key ID</Text>
						<TextInput style={styles.formInput} value={appleConfigData.keyId} onChangeText={handleKeyIdChange} placeholder="Enter your APNs Key ID" placeholderTextColor="#666" />
						<Text style={styles.formHelp}>This is displayed on the key details page after creation</Text>
					</View>

					<View style={styles.formGroup}>
						<Text style={styles.formLabel}>APNs Authentication Key (.p8)</Text>
						<View style={styles.fileUploadContainer}>
							<Text style={styles.fileNameText}>{appleConfigData.p8FileName || "No file selected"}</Text>
							<TouchableOpacity style={styles.fileUploadButton} onPress={() => document.getElementById("p8FileUpload").click()}>
								<Text style={styles.fileUploadButtonText}>Choose File</Text>
							</TouchableOpacity>
							<input id="p8FileUpload" type="file" accept=".p8" onChange={handleFileUpload} style={{ display: "none" }} />
						</View>
						<Text style={styles.formHelp}>{appleConfigData.p8FileName && !appleConfigData.p8Key ? "Current file will be kept unless you upload a new one" : "Upload the .p8 file you downloaded (you can only download it once)"}</Text>
					</View>

					{error && (
						<View style={styles.errorContainer}>
							<Text style={styles.errorText}>{error}</Text>
						</View>
					)}
				</View>
			</View>
		);
	};

	const renderStepContent = () => {
		switch (currentStep) {
			case 1:
				return renderStepOne();
			case 2:
				return renderStepTwo();
			case 3:
				return renderStepThree();
			case 4:
				return renderStepFour();
			default:
				return null;
		}
	};

	const renderStepActions = () => {
		return (
			<View style={styles.stepActionsContainer}>
				{
					<TouchableOpacity style={[styles.backButton, { opacity: currentStep > 1 ? 1 : 0 }]} onPress={handlePrevStep}>
						<Ionicons name="arrow-back" size={16} color="#fff" />
						<Text style={styles.backButtonText}>Back</Text>
					</TouchableOpacity>
				}

				<View style={styles.actionsRight}>
					{currentStep < 4 ? (
						<TouchableOpacity style={styles.nextButton} onPress={handleNextStep}>
							<Text style={styles.nextButtonText}>Next</Text>
							<Ionicons name="arrow-forward" size={16} color="#fff" />
						</TouchableOpacity>
					) : (
						<TouchableOpacity style={[styles.saveButton, !appleConfigData.keyId && styles.saveButtonDisabled]} onPress={handleSaveAppleConfig} disabled={!appleConfigData.keyId}>
							{configSaving ? (
								<View style={styles.buttonContent}>
									<Animated.View style={{ transform: [{ rotate: spin }] }}>
										<Ionicons name="refresh" size={16} color="#fff" />
									</Animated.View>
									<Text style={styles.saveButtonText}>Saving...</Text>
								</View>
							) : (
								<Text style={styles.saveButtonText}>Save Configuration</Text>
							)}
						</TouchableOpacity>
					)}
				</View>
			</View>
		);
	};

	const renderAppleConfigModal = () => {
		return (
			<View style={styles.modalOverlay}>
				<View style={styles.modal}>
					<View style={styles.modalHeader}>
						<Text style={styles.modalTitle}>Configure Apple Push Notifications</Text>
						<TouchableOpacity onPress={() => setShowAppleConfigModal(false)}>
							<Ionicons name="close" size={24} color="#fff" />
						</TouchableOpacity>
					</View>

					{renderStepIndicator()}

					<View style={styles.modalContent}>{renderStepContent()}</View>

					<View style={styles.modalFooter}>{renderStepActions()}</View>
				</View>
			</View>
		);
	};

	const renderErrorDialog = () => {
		return (
			<View style={styles.modalOverlay}>
				<View style={[styles.modal, { maxWidth: 450 }]}>
					<View style={styles.modalHeader}>
						<Text style={styles.modalTitle}>Dependency Not Met</Text>
						<TouchableOpacity onPress={() => setShowErrorDialog(false)}>
							<Ionicons name="close" size={24} color="#fff" />
						</TouchableOpacity>
					</View>
					<View style={styles.modalContent}>
						<View style={styles.errorDialogContent}>
							<Ionicons name="alert-circle" size={48} color="#FF453A" style={styles.errorIcon} />
							<Text style={styles.errorDialogTitle}>Apple Core Service Not Active</Text>
							<Text style={styles.errorDialogText}>Please ensure the Apple Developer Core Service is in active state before configuring push notifications.</Text>
						</View>
						<View style={[styles.modalActions, { justifyContent: "center" }]}>
							<TouchableOpacity style={styles.createButton} onPress={() => setShowErrorDialog(false)}>
								<Text style={styles.createButtonText}>Understood!</Text>
							</TouchableOpacity>
						</View>
					</View>
				</View>
			</View>
		);
	};

	const renderAppleConfigSection = () => {
		const isAppleCoreActive = appConfig?.isAppleCoreActive;
		const teamApnConfig = selectedTeam?.services?.appleNotificationConfig;
		const hasTeamConfig = teamApnConfig && teamApnConfig.keyId;
		const isServiceActive = appConfig?.serviceStatus === "active";

		// Determine button state
		let buttonState = "locked";
		let buttonText = "Locked";
		let buttonIcon = "lock-closed";
		let buttonColor = "#FF453A";

		if (isAppleCoreActive) {
			if (hasTeamConfig && isServiceActive) {
				buttonState = "edit";
				buttonText = "Modify";
				buttonIcon = "pencil";
				buttonColor = "#0A84FF";
			} else if (hasTeamConfig) {
				buttonState = "add";
				buttonText = configSaving ? "Adding..." : "Add to App";
				buttonIcon = configSaving ? "refresh" : "add";
				buttonColor = "#0A84FF";
			} else {
				buttonState = "configure";
				buttonText = "Configure";
				buttonIcon = "add";
				buttonColor = "#0A84FF";
			}
		}

		return (
			<View style={styles.configSection}>
				<View style={styles.configHeader}>
					<Text style={styles.configTitle}>Apple Push Notifications</Text>
					<TouchableOpacity style={[styles.configButton, buttonState === "locked" ? styles.lockedButton : buttonState === "edit" ? styles.editButton : styles.addButton]} onPress={handleEditAppleConfig} disabled={configSaving}>
						{configSaving && buttonState === "add" ? (
							<View style={styles.buttonContent}>
								<Animated.View style={{ transform: [{ rotate: spin }] }}>
									<Ionicons name="refresh" size={16} color={buttonColor} />
								</Animated.View>
								<Text style={[styles.configButtonText, { color: buttonColor }]}>{buttonText}</Text>
							</View>
						) : (
							<>
								<Ionicons name={buttonIcon} size={16} color={buttonColor} />
								<Text style={[styles.configButtonText, { color: buttonColor }]}>{buttonText}</Text>
							</>
						)}
					</TouchableOpacity>
				</View>

				{isServiceActive ? (
					<View style={styles.configDetails}>
						<View style={styles.configItem}>
							<Text style={styles.configLabel}>Team</Text>
							<Text style={styles.configValue}>{selectedTeam?.name || "Unknown Team"}</Text>
						</View>
						<View style={styles.configItem}>
							<Text style={styles.configLabel}>Key ID</Text>
							<Text style={styles.configValue}>{teamApnConfig?.keyId || "Not configured"}</Text>
						</View>
						<View style={styles.configItem}>
							<Text style={styles.configLabel}>Authentication Key</Text>
							<Text style={styles.configValue}>{teamApnConfig?.p8FileName || "Not uploaded"}</Text>
						</View>
						<View style={styles.configStatus}>
							<View style={[styles.statusDot, styles.activeStatusDot]} />
							<Text style={styles.statusText}>Active</Text>
						</View>
					</View>
				) : hasTeamConfig ? (
					<View style={styles.configDetails}>
						<View style={styles.configItem}>
							<Text style={styles.configLabel}>Team</Text>
							<Text style={styles.configValue}>{selectedTeam?.name || "Unknown Team"}</Text>
						</View>
						<View style={styles.configItem}>
							<Text style={styles.configLabel}>Key ID</Text>
							<Text style={styles.configValue}>{teamApnConfig?.keyId || "Not configured"}</Text>
						</View>
						<View style={styles.configItem}>
							<Text style={styles.configLabel}>Authentication Key</Text>
							<Text style={styles.configValue}>{teamApnConfig?.p8FileName || "Not uploaded"}</Text>
						</View>
						<View style={styles.configStatus}>
							<View style={[styles.statusDot, { backgroundColor: "#FFC107" }]} />
							<Text style={[styles.statusText, { color: "#FFC107" }]}>Ready to Add to App</Text>
						</View>
					</View>
				) : (
					<View style={styles.emptyConfig}>
						<Text style={styles.emptyConfigText}>Apple Push Notifications not configured</Text>
						<Text style={styles.emptyConfigSubtext}>
							{isAppleCoreActive
								? "Configure Apple Push Notifications to send notifications to iOS devices. You'll need to upload your APNs Authentication Key (.p8) from the Apple Developer Portal."
								: "Apple Developer Core Service must be active before configuring push notifications."}
						</Text>
					</View>
				)}
			</View>
		);
	};

	const renderGoogleConfigSection = () => {
		return (
			<View style={styles.configSection}>
				<View style={styles.configHeader}>
					<Text style={styles.configTitle}>Google Push Notifications</Text>
					<View style={styles.disabledBadge}>
						<Text style={styles.disabledBadgeText}>Coming Soon</Text>
					</View>
				</View>
				<View style={styles.emptyConfig}>
					<Text style={styles.emptyConfigText}>Google Push Notifications not available yet</Text>
					<Text style={styles.emptyConfigSubtext}>Support for Google Push Notifications is coming soon. This will allow you to send notifications to Android devices.</Text>
				</View>
			</View>
		);
	};

	if (loading.app || loading.team) {
		return (
			<View style={styles.container}>
				<View style={styles.loadingContainer}>
					<Animated.View style={{ transform: [{ rotate: spin }] }}>
						<Ionicons name="refresh" size={24} color="#fff" />
					</Animated.View>
					<Text style={styles.loadingText}>Loading push notification settings...</Text>
				</View>
			</View>
		);
	}

	return (
		<View style={styles.container}>
			<View style={styles.header}>
				<Text style={styles.title}>Push Notifications</Text>
				<Text style={styles.subtitle}>Configure push notification services for your app. This allows you to send notifications to your users' devices.</Text>
			</View>

			{error && (
				<View style={styles.errorContainer}>
					<Text style={styles.errorText}>{error}</Text>
				</View>
			)}

			{renderAppleConfigSection()}
			{renderGoogleConfigSection()}

			{showAppleConfigModal && renderAppleConfigModal()}
			{showErrorDialog && renderErrorDialog()}
		</View>
	);
};

const styles = StyleSheet.create({
	container: {
		flex: 1,
		backgroundColor: "#333",
		borderRadius: 12,
		padding: 20,
	},
	header: {
		marginBottom: 24,
	},
	title: {
		fontSize: 24,
		fontWeight: "700",
		color: "#fff",
		marginBottom: 8,
	},
	subtitle: {
		fontSize: 14,
		color: "#999",
		lineHeight: 20,
	},
	loadingContainer: {
		flex: 1,
		justifyContent: "center",
		alignItems: "center",
		gap: 16,
	},
	loadingText: {
		color: "#fff",
		fontSize: 16,
	},
	errorContainer: {
		backgroundColor: "#FF453A15",
		padding: 12,
		borderRadius: 8,
		marginBottom: 16,
	},
	errorText: {
		color: "#FF453A",
		fontSize: 14,
	},
	configSection: {
		backgroundColor: "#2A2A2A",
		borderRadius: 12,
		padding: 20,
		marginBottom: 24,
	},
	configHeader: {
		flexDirection: "row",
		justifyContent: "space-between",
		alignItems: "center",
		marginBottom: 16,
	},
	configTitle: {
		fontSize: 18,
		fontWeight: "600",
		color: "#fff",
	},
	configButton: {
		flexDirection: "row",
		alignItems: "center",
		gap: 8,
		padding: 8,
		borderRadius: 8,
	},
	editButton: {
		backgroundColor: "#0A84FF15",
	},
	addButton: {
		backgroundColor: "#0A84FF15",
	},
	lockedButton: {
		backgroundColor: "#FF453A15",
	},
	configButtonText: {
		fontSize: 14,
		fontWeight: "500",
	},
	activeButtonText: {
		color: "#0A84FF",
	},
	lockedButtonText: {
		color: "#FF453A",
	},
	configDetails: {
		backgroundColor: "#33333350",
		borderRadius: 8,
		padding: 16,
	},
	configItem: {
		marginBottom: 12,
	},
	configLabel: {
		color: "#666",
		fontSize: 12,
		marginBottom: 4,
	},
	configValue: {
		color: "#fff",
		fontSize: 14,
		fontFamily: "monospace",
	},
	configStatus: {
		flexDirection: "row",
		alignItems: "center",
		gap: 8,
		marginTop: 16,
		paddingTop: 16,
		borderTopWidth: 1,
		borderTopColor: "#404040",
	},
	statusDot: {
		width: 8,
		height: 8,
		borderRadius: 4,
	},
	activeStatusDot: {
		backgroundColor: "#82C91E",
	},
	statusText: {
		color: "#82C91E",
		fontSize: 14,
		fontWeight: "500",
	},
	emptyConfig: {
		padding: 24,
		alignItems: "center",
		backgroundColor: "#33333350",
		borderRadius: 8,
	},
	emptyConfigText: {
		color: "#fff",
		fontSize: 15,
		fontWeight: "500",
		marginBottom: 8,
		textAlign: "center",
	},
	emptyConfigSubtext: {
		color: "#666",
		fontSize: 13,
		textAlign: "center",
		lineHeight: 18,
	},
	modalOverlay: {
		position: "absolute",
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		backgroundColor: "rgba(0, 0, 0, 0.3)",
		justifyContent: "center",
		alignItems: "center",
		zIndex: 3,
	},
	modal: {
		backgroundColor: "#2A2A2A",
		borderRadius: 12,
		width: "70%",
		maxWidth: 800,
		maxHeight: "90%",
		display: "flex",
		flexDirection: "column",
	},
	modalHeader: {
		flexDirection: "row",
		justifyContent: "space-between",
		alignItems: "center",
		padding: 20,
		borderBottomWidth: 1,
		borderBottomColor: "#333",
	},
	modalTitle: {
		fontSize: 20,
		fontWeight: "600",
		color: "#fff",
	},
	modalContent: {
		padding: 24,
		flex: 1,
		overflow: "auto",
	},
	modalFooter: {
		borderTopWidth: 1,
		borderTopColor: "#333",
		padding: 16,
	},
	stepIndicatorContainer: {
		flexDirection: "row",
		backgroundColor: "#222",
		padding: 12,
		borderBottomWidth: 1,
		borderBottomColor: "#333",
	},
	stepIndicatorItem: {
		flex: 1,
		alignItems: "center",
		position: "relative",
	},
	stepDot: {
		width: 12,
		height: 12,
		borderRadius: 6,
		backgroundColor: "#444",
		marginBottom: 4,
	},
	activeStepDot: {
		backgroundColor: "#0A84FF",
	},
	stepText: {
		fontSize: 12,
		color: "#666",
	},
	activeStepText: {
		color: "#fff",
		fontWeight: "500",
	},
	stepContainer: {
		flex: 1,
	},
	stepHeading: {
		fontSize: 20,
		fontWeight: "600",
		color: "#fff",
		marginBottom: 16,
	},
	stepContentBox: {
		backgroundColor: "#33333350",
		borderRadius: 8,
		padding: 20,
	},
	stepInstructions: {
		color: "#fff",
		fontSize: 15,
		marginBottom: 20,
		lineHeight: 22,
	},
	stepActionContainer: {
		marginBottom: 20,
	},
	stepActionButton: {
		backgroundColor: "#0A84FF",
		borderRadius: 8,
		padding: 12,
		flexDirection: "row",
		alignItems: "center",
		justifyContent: "center",
		gap: 8,
	},
	stepActionButtonText: {
		color: "#fff",
		fontSize: 14,
		fontWeight: "500",
	},
	stepNote: {
		color: "#999",
		fontSize: 13,
		fontStyle: "italic",
		lineHeight: 18,
	},
	stepNoteDanger: {
		color: "#FF453A",
		fontSize: 13,
		fontStyle: "italic",
		lineHeight: 18,
	},
	configInstructionItem: {
		flexDirection: "row",
		marginBottom: 16,
		alignItems: "flex-start",
	},
	configInstructionNumber: {
		width: 24,
		height: 24,
		borderRadius: 12,
		backgroundColor: "#0A84FF",
		textAlign: "center",
		lineHeight: 24,
		color: "#fff",
		fontWeight: "600",
		marginRight: 12,
	},
	configInstructionText: {
		flex: 1,
		color: "#fff",
		fontSize: 14,
		lineHeight: 20,
	},
	highlightText: {
		color: "#0A84FF",
		fontWeight: "600",
	},
	codeText: {
		fontFamily: "monospace",
		backgroundColor: "#222",
		color: "#fff",
		padding: 4,
		borderRadius: 4,
		marginLeft: 4,
	},
	teamInfoContainer: {
		backgroundColor: "#222",
		borderRadius: 8,
		padding: 16,
		marginBottom: 20,
	},
	teamInfoLabel: {
		color: "#999",
		fontSize: 12,
		marginBottom: 12,
	},
	teamInfoItem: {
		flexDirection: "row",
		alignItems: "center",
		marginBottom: 8,
	},
	teamInfoTitle: {
		color: "#999",
		fontSize: 14,
		width: 100,
	},
	teamInfoValue: {
		color: "#fff",
		fontSize: 14,
		fontFamily: "monospace",
		flex: 1,
	},
	teamInfoLock: {
		marginLeft: 8,
	},
	formHelp: {
		color: "#999",
		fontSize: 12,
		marginTop: 4,
	},
	stepActionsContainer: {
		flexDirection: "row",
		justifyContent: "space-between",
		alignItems: "center",
	},
	actionsRight: {
		flexDirection: "row",
		gap: 12,
	},
	backButton: {
		flexDirection: "row",
		alignItems: "center",
		gap: 8,
		padding: 12,
		borderRadius: 8,
		backgroundColor: "#333",
	},
	backButtonText: {
		color: "#fff",
		fontSize: 14,
		fontWeight: "500",
	},
	nextButton: {
		flexDirection: "row",
		alignItems: "center",
		gap: 8,
		padding: 12,
		borderRadius: 8,
		backgroundColor: "#0A84FF",
	},
	nextButtonText: {
		color: "#fff",
		fontSize: 14,
		fontWeight: "500",
	},
	saveButton: {
		padding: 12,
		borderRadius: 8,
		backgroundColor: "#0A84FF",
		minWidth: 160,
		alignItems: "center",
	},
	saveButtonDisabled: {
		backgroundColor: "#0A84FF50",
	},
	saveButtonText: {
		color: "#fff",
		fontSize: 14,
		fontWeight: "500",
	},
	buttonContent: {
		flexDirection: "row",
		alignItems: "center",
		justifyContent: "center",
		gap: 8,
	},
	disabledBadge: {
		backgroundColor: "#333",
		borderRadius: 12,
		paddingVertical: 4,
		paddingHorizontal: 8,
	},
	disabledBadgeText: {
		color: "#999",
		fontSize: 12,
		fontWeight: "500",
	},
	errorDialogContent: {
		alignItems: "center",
		padding: 16,
	},
	errorIcon: {
		marginBottom: 16,
	},
	errorDialogTitle: {
		fontSize: 18,
		fontWeight: "600",
		color: "#fff",
		marginBottom: 12,
		textAlign: "center",
	},
	errorDialogText: {
		fontSize: 14,
		color: "#999",
		textAlign: "center",
		lineHeight: 20,
	},
	formGroup: {
		marginBottom: 20,
	},
	formLabel: {
		color: "#fff",
		fontSize: 14,
		fontWeight: "500",
		marginBottom: 8,
	},
	formInput: {
		backgroundColor: "#333",
		borderRadius: 8,
		padding: 12,
		color: "#fff",
		fontSize: 14,
		borderWidth: 1,
		borderColor: "#444",
	},
	fileUploadContainer: {
		flexDirection: "row",
		alignItems: "center",
		backgroundColor: "#333",
		borderRadius: 8,
		padding: 12,
		gap: 12,
		borderWidth: 1,
		borderColor: "#444",
	},
	fileNameText: {
		flex: 1,
		color: "#fff",
		fontSize: 14,
		fontFamily: "monospace",
	},
	fileUploadButton: {
		backgroundColor: "#0A84FF",
		borderRadius: 6,
		paddingVertical: 8,
		paddingHorizontal: 12,
	},
	fileUploadButtonText: {
		color: "#fff",
		fontSize: 13,
		fontWeight: "500",
	},
	appleButton: {
		backgroundColor: "#fff",
		borderRadius: 4,
		paddingVertical: 4,
		paddingHorizontal: 12,
		marginHorizontal: 4,
		display: "inline-flex",
		borderWidth: 1,
		borderColor: "#0070c9",
		padding: 5,
	},
	appleButtonText: {
		color: "#0070c9",
		fontSize: 13,
		fontWeight: "600",
	},
	appleButtonContainer: {
		backgroundColor: "#fff",
		padding: 5,
		marginHorizontal: 4,
		borderRadius: 4,
	},
});

export default PushNotificationsService;
